import { useEffect } from "react";

interface SeoProps {
  title: string;
  description: string;
  canonicalPath?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  robots?: string;
  ogType?: "website" | "article";
  publishedTime?: string;
  modifiedTime?: string;
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_URL = "https://hiredcoach.ai";

const upsertMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
};

const upsertLink = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
};

const removeElement = (selector: string) => {
  document.head.querySelector(selector)?.remove();
};

const upsertStructuredData = (data: Record<string, unknown> | Record<string, unknown>[]) => {
  let script = document.head.querySelector('script[data-seo="structured-data"]') as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.seo = "structured-data";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
};

export const Seo = ({
  title,
  description,
  canonicalPath = "/",
  keywords,
  ogTitle,
  ogDescription,
  robots,
  ogType = "website",
  publishedTime,
  modifiedTime,
  structuredData,
}: SeoProps) => {
  useEffect(() => {
    document.title = title;

    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "HiredCoach" });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: ogType });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: ogTitle || title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: ogDescription || description });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: `${SITE_URL}${canonicalPath}` });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: ogTitle || title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: ogDescription || description });

    if (keywords) {
      upsertMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
    } else {
      removeElement('meta[name="keywords"]');
    }

    if (robots) {
      upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
    } else {
      removeElement('meta[name="robots"]');
    }

    if (publishedTime) {
      upsertMeta('meta[property="article:published_time"]', {
        property: "article:published_time",
        content: publishedTime,
      });
    } else {
      removeElement('meta[property="article:published_time"]');
    }

    if (modifiedTime) {
      upsertMeta('meta[property="article:modified_time"]', {
        property: "article:modified_time",
        content: modifiedTime,
      });
    } else {
      removeElement('meta[property="article:modified_time"]');
    }

    upsertLink('link[rel="canonical"]', { rel: "canonical", href: `${SITE_URL}${canonicalPath}` });

    if (structuredData) {
      upsertStructuredData(structuredData);
    } else {
      removeElement('script[data-seo="structured-data"]');
    }
  }, [
    canonicalPath,
    description,
    keywords,
    modifiedTime,
    ogDescription,
    ogTitle,
    ogType,
    publishedTime,
    robots,
    structuredData,
    title,
  ]);

  return null;
};

export default Seo;
